import type { Media } from 'types/media';

import React from 'react';

import { Button, Paragraph } from '@leafwell/components';

import HeadingBody from './heading-body';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';

type ProductCtaProps = {
  title?: string;
  image?: Media;
  description?: string;
  content?: string;
  listItems?: string[];
  buttonLabel?: string;
  buttonRef?: string;
  buttonTarget?: string;
};

const ProductCta: React.FC<ProductCtaProps> = ({
  title,
  image,
  description,
  content,
  listItems,
  buttonLabel,
  buttonRef,
  buttonTarget,
}) => {
  const { trackEvent } = useEventsTracker();

  return (
    <div className="relative not-prose grid md:grid-cols-auto-auto gap-6 md:justify-items-center rounded-3xl bg-light-purple-gradient pt-4 pl-6 pr-6 pb-0 my-6 md:w-[745px]">
      <div className="flex flex-col gap-y-3 xl:gap-y-2 mb-4 md:mb-0">
        {title ? (
          <HeadingBody
            level="5"
            tagName="h2"
            title={title}
            className="my-reset text-purple-600 font-semibold !text-[23px] !leading-[30px] lg:!text-[29px] lg:!leading-[36px]"
          />
        ) : null}
        {description ? (
          <a
            href={buttonRef ? buttonRef : '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Paragraph
              className="triangle-list-type text-yellow-600 hover:text-yellow-500 hover:underline font-semibold my-reset !text-[18px] !leading-[24px] lg:!text-[23px] lg:!leading-[30px]"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </a>
        ) : null}
        {content ? (
          <Paragraph
            className="my-reset !text-sm lg:!text-base"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null}
        {listItems?.length > 0 ? (
          <ul className="list-disc list-inside">
            {listItems?.map(item => (
              <li className="text-sm lg:text-base">{item}</li>
            ))}
          </ul>
        ) : null}
        {buttonRef ? (
          <Button
            href={buttonRef}
            label={buttonLabel}
            rounded={true}
            filled={true}
            variant="secondary"
            target={buttonTarget}
            className="w-fit md:absolute md:top-2/3 md:right-[20%] z-10"
            icon="arrow"
            onClick={() => {
              trackEvent({
                event: 'select_content',
                content_type: buttonLabel,
                action: 'ProductCta-click-cta',
                origin: window?.location?.pathname,
                service: buttonRef?.includes('medical-card')
                  ? 'medical_card'
                  : buttonRef?.includes('membership')
                  ? 'membership'
                  : 'telepharmacist',
              });
            }}
          />
        ) : null}
      </div>
      <div className="hidden md:block md:relative md:h-[250px] md:overflow-hidden">
        <Figure {...image} />
      </div>
    </div>
  );
};

export default ProductCta;
